/**
 * 콘텐츠가 품은 그림·영상의 콘솔 미리보기 주소(2026-10-30) — 올린 HTML 의 영상(`HtmlContent.tsx`)과 반반 화면 템플릿의
 * 그림·영상·HTML(`TemplatePreview.tsx`). 앱이 뜰 때 묻는 곳을 정한다(`App.tsx`). 정하지 않았으면 그 자리는 빈다(템플릿은 장식 무늬).
 *
 * 반반 화면의 HTML 칸(2026-11-03)은 주소만으로 모자란다 — 고정 크기로 만든 HTML 은 그 크기로 그려 칸에 맞추므로 기준 크기·맞춤도
 * 함께 받는다(`MediaInfo`).
 */
import { useEffect, useState } from 'react';
import type { Asset } from '../api/client.js';

/** 미리보기에 쓰는 자산의 몇 칸 — 주소와, 올린 HTML 이면 기준 크기(`width`·`height`)·화면 맞춤. */
export type MediaInfo = Pick<Asset, 'type' | 'preview_url' | 'width' | 'height' | 'html_fit'>;

let source: ((id: string) => Promise<MediaInfo | null>) | null = null;
/** 자산 id 마다 한 번만 묻는다(목록의 여러 칸이 같은 그림을 물어도). */
const infos = new Map<string, Promise<MediaInfo | null>>();

export function setMediaSource(next: ((id: string) => Promise<MediaInfo | null>) | null): void {
  source = next;
  infos.clear();
}

/** 이 자산의 미리보기 정보. 주소가 없으면 null — 다음에 다시 묻는다(변환 중이던 영상도 곧 생긴다). */
export function mediaInfo(id: string): Promise<MediaInfo | null> {
  const found = infos.get(id);
  if (found) return found;
  const asking = (source?.(id) ?? Promise.resolve(null))
    .then((info) => (info?.preview_url ? info : null))
    .catch(() => null);
  infos.set(id, asking);
  void asking.then((info) => {
    if (!info && infos.get(id) === asking) infos.delete(id);
  });
  return asking;
}

/** 이 자산의 미리보기 주소. 없으면 null. */
export function mediaUrl(id: string): Promise<string | null> {
  return mediaInfo(id).then((info) => info?.preview_url ?? null);
}

/** 여러 자산의 미리보기 정보 — 자산 id → 정보. 받은 것만 담긴다. */
export function useMediaInfos(ids: readonly string[]): Readonly<Record<string, MediaInfo>> {
  const key = ids.join(',');
  const [found, setFound] = useState<Record<string, MediaInfo>>({});
  useEffect(() => {
    let live = true;
    for (const id of key ? key.split(',') : []) {
      void mediaInfo(id).then((info) => {
        if (live && info) setFound((known) => (known[id] === info ? known : { ...known, [id]: info }));
      });
    }
    return () => {
      live = false;
    };
  }, [key]);
  return found;
}

/** 여러 자산의 미리보기 주소 — 자산 id → 주소. 받은 것만 담긴다. 같은 목록이면 같은 객체를 돌려준다. */
export function useMediaUrls(ids: readonly string[]): Readonly<Record<string, string>> {
  const key = ids.join(',');
  const [found, setFound] = useState<Record<string, string>>({});
  useEffect(() => {
    let live = true;
    for (const id of key ? key.split(',') : []) {
      void mediaUrl(id).then((url) => {
        if (live && url) setFound((known) => (known[id] === url ? known : { ...known, [id]: url }));
      });
    }
    return () => {
      live = false;
    };
  }, [key]);
  return found;
}
